import { cva } from 'class-variance-authority';

/**
 * The indicator's interactive target.
 *
 * `size-8` is 32×32 px — the minimum comfortable target for a control sitting
 * next to other controls. It is spelled with the Tailwind scale rather than an
 * arbitrary `h-[32px]`, because the repo's typography lint rejects literal px
 * anywhere in a source file. `min-h-8 min-w-8` keeps it from being squashed by a
 * flex parent.
 */
export const emrSyncIndicatorVariants = cva(
  'relative inline-flex size-8 min-h-8 min-w-8 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
  {
    variants: {
      // Two states only. A mismatch is layered ON TOP of `synchronized` (see
      // emrSyncMismatchOverlayVariants) rather than competing as a third state:
      // the patient IS linked, there are just conflicts left to resolve.
      state: {
        synchronized:
          'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-50 dark:text-emerald-900',
        pending:
          'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-50 dark:text-amber-900',
      },
    },
    defaultVariants: {
      state: 'synchronized',
    },
  },
);

/**
 * The mismatch marker: a small dot pinned to the badge's top-right corner.
 *
 * Deliberately the loudest treatment in the component — an unresolved
 * Portal-vs-EMR conflict is the one thing on a Synchronized badge that still
 * needs a human. `ring-background` punches it out of the badge behind it so it
 * stays legible on either state colour.
 *
 * Inset INSIDE the badge rather than straddling its corner: at `top-0.5 right-0.5`
 * the 2px ring lands flush with the 32px badge edge, so the marker never paints
 * outside its own bounds. A negative offset put it 2-3px proud of the badge, where
 * any ancestor that clips (a dense table cell, a row with fixed height) cropped the
 * top of the dot. The badge has ~9px of clearance around its 14px icon, so nothing
 * is crowded by moving it in.
 */
export const emrSyncMismatchOverlayVariants = cva(
  'pointer-events-none absolute right-0.5 top-0.5 size-2.5 rounded-full bg-destructive ring-2 ring-background',
);

/**
 * The `pill` variant: a labelled capsule for roomy surfaces (a form section
 * header), where naming the state outright beats making the reader decode an
 * icon.
 *
 * It exists ALONGSIDE the icon variant rather than replacing it because this
 * indicator also renders in dense surfaces — a width-8 table column, inline next
 * to a patient name — where a text capsule does not fit. Same component, same
 * states, two densities.
 */
export const emrSyncPillVariants = cva(
  'relative inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
  {
    variants: {
      state: {
        synchronized:
          'border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-50 dark:text-emerald-900',
        pending:
          'border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-50 dark:text-amber-900',
      },
    },
    defaultVariants: {
      state: 'synchronized',
    },
  },
);

/**
 * The pill's trailing glyph wrapper. Carries the SAME state icon the dense `icon`
 * variant draws, so one badge is recognisable at both densities instead of the
 * pill inventing a second visual language for the same fact.
 *
 * `relative` anchors the conflict marker below.
 */
export const emrSyncPillIconVariants = cva('relative shrink-0');

/**
 * The pill's conflict marker: the same red dot as the dense badge, pinned to the
 * corner of the glyph rather than of the capsule.
 *
 * A small negative offset is safe HERE — unlike on the bare icon badge — because
 * the pill's own `py-1`/`px-3` padding leaves clearance on every side, so the dot
 * still paints inside the capsule and cannot be cropped by an ancestor.
 */
export const emrSyncPillMismatchOverlayVariants = cva(
  'pointer-events-none absolute -right-1 -top-1 size-2.5 rounded-full bg-destructive ring-2 ring-background',
);
